import { FormEvent, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";

interface Announcement {
  id: string;
  title: string;
  message: string | null;
  link_label: string | null;
  link_url: string | null;
  start_at: string | null;
  end_at: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

type Filter = "all" | "active" | "inactive";

const emptyDraft = (): Partial<Announcement> => ({
  title: "",
  message: "",
  link_label: "",
  link_url: "",
  start_at: null,
  end_at: null,
  is_active: true,
  sort_order: 0,
});

const toLocalInput = (iso: string | null) => (iso ? iso.slice(0, 16) : "");
const fromLocalInput = (val: string) => (val ? new Date(val).toISOString() : null);

const AdminAnnouncements = () => {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [editing, setEditing] = useState<Partial<Announcement> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Announcements | Hadassah Homes Admin";
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("announcements")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    setRows((data ?? []) as Announcement[]);
    setLoading(false);
  };

  const filtered = useMemo(
    () =>
      rows.filter((r) =>
        filter === "all" ? true : filter === "active" ? r.is_active : !r.is_active
      ),
    [rows, filter]
  );

  const openNew = () => setEditing(emptyDraft());
  const openEdit = (row: Announcement) => setEditing(row);

  const toggleActive = async (row: Announcement) => {
    const { data, error: err } = await supabase
      .from("announcements")
      .update({ is_active: !row.is_active, updated_by: user?.id ?? null })
      .eq("id", row.id)
      .select()
      .single();
    if (err) {
      toast.error(err.message);
      return;
    }
    setRows((prev) => prev.map((r) => (r.id === row.id ? (data as Announcement) : r)));
    toast.success(row.is_active ? "Deactivated" : "Activated");
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const title = (editing.title ?? "").trim();
    if (!title) {
      toast.error("Title is required");
      return;
    }
    if (editing.link_url && !editing.link_label) {
      toast.error("Link label is required when a link URL is set");
      return;
    }
    setSaving(true);
    const payload = {
      title,
      message: editing.message?.trim() || null,
      link_label: editing.link_label?.trim() || null,
      link_url: editing.link_url?.trim() || null,
      start_at: editing.start_at ?? null,
      end_at: editing.end_at ?? null,
      is_active: editing.is_active ?? true,
      sort_order: Number(editing.sort_order ?? 0) || 0,
      updated_by: user?.id ?? null,
    };

    if (editing.id) {
      const { data, error: err } = await supabase
        .from("announcements")
        .update(payload)
        .eq("id", editing.id)
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => prev.map((r) => (r.id === editing.id ? (data as Announcement) : r)));
      toast.success("Announcement updated");
    } else {
      const { data, error: err } = await supabase
        .from("announcements")
        .insert({ ...payload, created_by: user?.id ?? null })
        .select()
        .single();
      setSaving(false);
      if (err) return toast.error(err.message);
      setRows((prev) => [data as Announcement, ...prev]);
      toast.success("Announcement created");
    }
    setEditing(null);
  };

  return (
    <AdminLayout
      title="Announcements"
      description="Manage messages that appear in the top announcement bar. Inactive announcements are hidden but not deleted."
      publicHref="/"
      publicLabel="View top bar"
    >
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
              className="h-10 rounded-none border border-input bg-background px-3 text-sm"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => void load()}
              disabled={loading}
              className="rounded-none"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="gold" onClick={openNew} className="rounded-none">
              <Plus className="mr-2 h-4 w-4" />
              New announcement
            </Button>
          </div>
        </div>

        <div className="border border-border bg-card">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="p-6 text-destructive">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-muted-foreground">
                No announcements yet. Create one to display in the top bar.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3 hidden md:table-cell">Window</th>
                    <th className="px-4 py-3">Order</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td
                        className="cursor-pointer px-4 py-3 font-medium text-foreground"
                        onClick={() => openEdit(r)}
                      >
                        <div className="line-clamp-2">{r.title}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                        {r.start_at || r.end_at ? (
                          <span className="text-xs">
                            {r.start_at ? new Date(r.start_at).toLocaleDateString() : "—"} →{" "}
                            {r.end_at ? new Date(r.end_at).toLocaleDateString() : "—"}
                          </span>
                        ) : (
                          <span className="text-xs">Always</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.sort_order}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                            r.is_active
                              ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {r.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleActive(r)}
                            className="rounded-none"
                          >
                            {r.is_active ? "Deactivate" : "Activate"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(r)}
                            className="rounded-none"
                          >
                            Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {filtered.length} of {rows.length} announcements
        </p>
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {editing ? (
            <form onSubmit={handleSave}>
              <SheetHeader>
                <SheetTitle className="font-headline text-2xl">
                  {editing.id ? "Edit announcement" : "New announcement"}
                </SheetTitle>
                <SheetDescription>
                  Active announcements appear in the public site top bar.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <Field label="Title (shown in the top bar) *">
                  <Input
                    required
                    value={editing.title ?? ""}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="rounded-none"
                  />
                </Field>

                <Field label="Optional message (longer text)">
                  <Textarea
                    rows={2}
                    value={editing.message ?? ""}
                    onChange={(e) => setEditing({ ...editing, message: e.target.value })}
                    className="rounded-none"
                  />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Link label">
                    <Input
                      value={editing.link_label ?? ""}
                      onChange={(e) => setEditing({ ...editing, link_label: e.target.value })}
                      placeholder="View event"
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Link URL">
                    <Input
                      value={editing.link_url ?? ""}
                      onChange={(e) => setEditing({ ...editing, link_url: e.target.value })}
                      placeholder="/events"
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start (optional)">
                    <Input
                      type="datetime-local"
                      value={toLocalInput(editing.start_at ?? null)}
                      onChange={(e) =>
                        setEditing({ ...editing, start_at: fromLocalInput(e.target.value) })
                      }
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="End (optional)">
                    <Input
                      type="datetime-local"
                      value={toLocalInput(editing.end_at ?? null)}
                      onChange={(e) =>
                        setEditing({ ...editing, end_at: fromLocalInput(e.target.value) })
                      }
                      className="rounded-none"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sort order">
                    <Input
                      type="number"
                      value={editing.sort_order ?? 0}
                      onChange={(e) =>
                        setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })
                      }
                      className="rounded-none"
                    />
                  </Field>
                  <Field label="Active">
                    <label className="mt-2 inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!editing.is_active}
                        onChange={(e) =>
                          setEditing({ ...editing, is_active: e.target.checked })
                        }
                      />
                      Show in top bar
                    </label>
                  </Field>
                </div>

                <div>
                  <p className="mb-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                    Top bar preview
                  </p>
                  <div className="border border-border bg-[#0F1F18] px-4 py-3 text-[13px] text-white/80">
                    <span className="text-[#C9A95C]">Upcoming Event:</span>{" "}
                    <span>{editing.title || "Your announcement title"}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="submit" variant="gold" disabled={saving} className="rounded-none">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditing(null)}
                    className="rounded-none"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          ) : null}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted-foreground">
      {label}
    </label>
    {children}
  </div>
);

export default AdminAnnouncements;
